/**
 * Vendor capability of an organization (Business Account Pages slice 2, migration 2026-10-06-312).
 * An ACTIVE organization_vendor_profiles row = the organization is a vendor. A vendor-only organization has no facility
 * type and is never listed on maps / the locator / redemption lists (server guard VENDOR_ONLY_UNLISTED).
 */

import { supabase } from "./supabase";
import type { UUID } from "../types/common.types";

export interface VendorProfile {
  organization_id: UUID;
  vendor_name: string;
  vendor_number: string;
  vendor_email: string;
  vendor_phone: string;
  associated_organization_id: UUID | null;
  is_active: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
  deactivated_at: string | null;
}

export interface VendorFields {
  vendor_name: string;
  vendor_number: string;
  vendor_email: string;
  vendor_phone: string;
  associated_organization_id?: UUID | null;
  notes?: string | null;
}

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

/** Client mirror of the table CHECKs (the server is authoritative). Returns field -> message. */
export const validateVendorFields = (
  f: VendorFields,
): Partial<Record<keyof VendorFields, string>> => {
  const errors: Partial<Record<keyof VendorFields, string>> = {};
  if (!f.vendor_name.trim()) {
    errors.vendor_name = "Vendor name is required";
  } else if (f.vendor_name.trim().length > 200) {
    errors.vendor_name = "200 characters at most";
  }
  if (!f.vendor_number.trim()) {
    errors.vendor_number = "Vendor number is required";
  } else if (f.vendor_number.trim().length > 50) {
    errors.vendor_number = "50 characters at most";
  }
  if (!f.vendor_email.trim()) {
    errors.vendor_email = "Vendor email is required";
  } else if (!EMAIL_RE.test(f.vendor_email.trim())) {
    errors.vendor_email = "Invalid email format";
  }
  const digits = f.vendor_phone.replace(/\D/g, "");
  if (!f.vendor_phone.trim()) {
    errors.vendor_phone = "Vendor phone is required";
  } else if (digits.length < 10 || digits.length > 15) {
    errors.vendor_phone = "Enter a phone number with 10–15 digits";
  }
  return errors;
};

export const describeVendorError = (message?: string): string => {
  const m = message || "";
  if (m.includes("organization_vendor_profiles_active_number")) {
    return "Another active vendor already uses this vendor number.";
  }
  if (m.includes("VENDOR_ONLY_UNLISTED")) {
    return "A vendor-only organization cannot have a physical address, map location or Facility ID (so it never appears on maps, the locator or redemption lists). Choose its facility type first to make it a Business + Vendor record, or remove the address.";
  }
  if (m.includes("VENDOR_NOT_ALLOWED")) {
    return "Only an administrator can create an organization.";
  }
  if (m.includes("organization_vendor_profiles_email_format")) {
    return "The vendor email is not valid.";
  }
  if (m.includes("organization_vendor_profiles_phone_digits")) {
    return "The vendor phone needs 10–15 digits.";
  }
  if (m.includes("row-level security")) {
    return "You do not have permission to change vendor details.";
  }
  return m || "The vendor details could not be saved.";
};

export const getVendorProfile = async (
  organizationId: UUID,
): Promise<VendorProfile | null> => {
  const { data, error } = await supabase
    .from("organization_vendor_profiles")
    .select("*")
    .eq("organization_id", organizationId)
    .maybeSingle();
  if (error) {
    throw error;
  }
  return (data as VendorProfile | null) ?? null;
};

/** Add vendor details to an existing organization (no duplicate organization), or edit / re-activate them. */
export const saveVendorProfile = async (
  organizationId: UUID,
  f: VendorFields,
  exists: boolean,
): Promise<void> => {
  const payload = {
    vendor_name: f.vendor_name.trim(),
    vendor_number: f.vendor_number.trim(),
    vendor_email: f.vendor_email.trim(),
    vendor_phone: f.vendor_phone.trim(),
    associated_organization_id: f.associated_organization_id || null,
    notes: f.notes?.trim() || null,
    is_active: true,
  };
  const { error } = exists
    ? await supabase
        .from("organization_vendor_profiles")
        .update(payload)
        .eq("organization_id", organizationId)
    : await supabase
        .from("organization_vendor_profiles")
        .insert({ organization_id: organizationId, ...payload });
  if (error) {
    throw error;
  }
};

/** Retire vendor details (the row is kept; is_active = false). */
export const deactivateVendorProfile = async (
  organizationId: UUID,
): Promise<void> => {
  const { error } = await supabase
    .from("organization_vendor_profiles")
    .update({ is_active: false })
    .eq("organization_id", organizationId);
  if (error) {
    throw error;
  }
};

export interface NewVendorOrganization extends VendorFields {
  name: string;
  legal_name?: string;
}

/** Vendor-only organization + its vendor details in one server transaction (administrators). */
export const createVendorOrganization = async (
  input: NewVendorOrganization,
): Promise<UUID> => {
  const { data, error } = await supabase.rpc("create_vendor_organization", {
    p_name: input.name.trim(),
    p_legal_name: input.legal_name?.trim() || null,
    p_vendor_name: input.vendor_name.trim(),
    p_vendor_number: input.vendor_number.trim(),
    p_vendor_email: input.vendor_email.trim(),
    p_vendor_phone: input.vendor_phone.trim(),
    p_associated_organization_id: input.associated_organization_id || null,
  });
  if (error) {
    throw error;
  }
  return data as UUID;
};

export interface VendorIndexRow {
  organization_id: UUID;
  vendor_only: boolean;
}

/**
 * Active vendors, for the Organizations list "Kind" filter and "Vendor" badge. Bounded: vendors are a small set;
 * if this ever approaches the limit the filter must move server-side.
 */
export const VENDOR_INDEX_LIMIT = 1000;
export const getActiveVendorIndex = async (): Promise<VendorIndexRow[]> => {
  const { data, error } = await supabase
    .from("organization_vendor_profiles")
    .select(
      "organization_id, organization:organizations!organization_vendor_profiles_organization_id_fkey(organization_type)",
    )
    .eq("is_active", true)
    .limit(VENDOR_INDEX_LIMIT);
  if (error) {
    throw error;
  }
  type Row = {
    organization_id: UUID;
    organization: { organization_type: string | null } | null;
  };
  return ((data ?? []) as unknown as Row[]).map((r) => ({
    organization_id: r.organization_id,
    vendor_only: !(r.organization?.organization_type || "").trim(),
  }));
};

/** Organization ids whose vendor name / number / email matches (for the list search). */
export const searchVendorOrganizationIds = async (
  term: string,
): Promise<UUID[]> => {
  const t = sanitizeSearchTerm(term);
  if (!t) {
    return [];
  }
  const { data, error } = await supabase
    .from("organization_vendor_profiles")
    .select("organization_id")
    .or(
      `vendor_name.ilike.*${t}*,vendor_number.ilike.*${t}*,vendor_email.ilike.*${t}*`,
    )
    .limit(200);
  if (error) {
    throw error;
  }
  return ((data ?? []) as { organization_id: UUID }[]).map(
    (r) => r.organization_id,
  );
};

/** PostgREST .or() filter text must not carry its own syntax (, ( ) * : " \) -- strip it. */
export const sanitizeSearchTerm = (term: string): string =>
  term
    .replace(/[,()*:"\\%]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 100);
